import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BoltIcon from '@mui/icons-material/Bolt';
import BubbleChartIcon from '@mui/icons-material/BubbleChart';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import { toast } from 'react-toastify';
import {
  getGameType,
  getBlitzDuration,
  getKeyPopLevel,
} from './typingTutorGames';
import { fetchTypingProgress, saveTypingSession } from './typingTutorApi';
import { requireStudentAuth, parseStudentData } from './typingTutorUtils';
import { resolveTargetWpm } from './typingTutorCurriculum';
import SpeedBlitzGame from './SpeedBlitzGame';
import KeyPopGame from './KeyPopGame';
import TypeRacerGame from './TypeRacerGame';
import './typingTutorTheme.css';

const ICON_MAP = {
  bolt: BoltIcon,
  bubble_chart: BubbleChartIcon,
  directions_run: DirectionsRunIcon,
};

export default function TypingTutorGamePlay() {
  const navigate = useNavigate();
  const { gameId, variantId } = useParams();
  const [targetWpm, setTargetWpm] = useState(35);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const lastSavedAttemptRef = useRef('');
  const student = parseStudentData();
  const game = getGameType(gameId);

  useEffect(() => {
    if (!requireStudentAuth(navigate)) return undefined;
    const fallback = resolveTargetWpm(student?.enrolled_courses);
    setTargetWpm(fallback);
    fetchTypingProgress()
      .then((p) => {
        if (p?.targetWpm) setTargetWpm(p.targetWpm);
      })
      .catch(() => {});
  }, [navigate, student?.enrolled_courses]);

  const handleComplete = async (result) => {
    if (saving || !game) return;
    const attemptKey = `${gameId}:${variantId}:${result.wpm}:${result.accuracy}`;
    if (lastSavedAttemptRef.current === attemptKey) return;
    lastSavedAttemptRef.current = attemptKey;

    let lessonTitle = game.title;
    if (gameId === 'speed_blitz') {
      const d = getBlitzDuration(variantId);
      lessonTitle = `${game.title} — ${d.label}`;
    } else if (gameId === 'key_pop') {
      const l = getKeyPopLevel(variantId);
      lessonTitle = `${game.title} — ${l.title}`;
    } else if (gameId === 'type_racer') {
      lessonTitle = `${game.title} — ${targetWpm} WPM Ghost`;
    }

    setSaving(true);
    try {
      const primaryCourse = (student?.enrolled_courses || [])[0] || null;
      await saveTypingSession({
        exerciseType: gameId,
        lessonId: variantId || gameId,
        lessonTitle,
        courseCode: primaryCourse,
        wpm: result.wpm,
        accuracy: result.accuracy,
        timeSpentSeconds: result.timeSpentSeconds,
        errors: result.errors,
        totalChars: result.totalChars,
        correctChars: result.correctChars,
      });
      setSaved(true);
      toast.success(`Saved: ${result.wpm} WPM — synced to My Journey`);
    } catch (err) {
      lastSavedAttemptRef.current = '';
      toast.error(err.response?.data?.message || 'Could not save session.');
    } finally {
      setSaving(false);
    }
  };

  const handleRetry = () => {
    lastSavedAttemptRef.current = '';
    setSaved(false);
  };

  const handleCancel = () => navigate('/student/typing-tutor/games');

  if (!game) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">Game not found.</Alert>
      </Container>
    );
  }

  const Icon = ICON_MAP[game.icon] || BoltIcon;
  let subtitle = game.subtitle;
  if (gameId === 'speed_blitz') subtitle = getBlitzDuration(variantId).label;
  if (gameId === 'key_pop') subtitle = getKeyPopLevel(variantId).title;

  const renderGame = () => {
    if (gameId === 'speed_blitz') {
      return (
        <SpeedBlitzGame
          durationSeconds={getBlitzDuration(variantId).seconds}
          targetWpm={targetWpm}
          onComplete={handleComplete}
          onRetry={handleRetry}
          onCancel={handleCancel}
        />
      );
    }
    if (gameId === 'key_pop') {
      return (
        <KeyPopGame
          level={getKeyPopLevel(variantId)}
          targetWpm={targetWpm}
          onComplete={handleComplete}
          onRetry={handleRetry}
          onCancel={handleCancel}
        />
      );
    }
    if (gameId === 'type_racer') {
      return (
        <TypeRacerGame
          targetWpm={targetWpm}
          onComplete={handleComplete}
          onRetry={handleRetry}
          onCancel={handleCancel}
        />
      );
    }
    return null;
  };

  return (
    <Box className="tt-root">
      <Box className="tt-header">
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={handleCancel} sx={{ color: '#fff' }}>
              <ArrowBackIcon />
            </IconButton>
            <Icon sx={{ color: '#fff' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight={700}>
                {game.title}
              </Typography>
              <Typography variant="caption" className="tt-sans" sx={{ opacity: 0.9 }}>
                {subtitle} — Goal {targetWpm} WPM
              </Typography>
            </Box>
            {saving && <CircularProgress size={22} sx={{ color: '#fff' }} />}
          </Box>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 3 }}>
        {renderGame()}
        {saved && (
          <Alert severity="success" className="tt-sans" sx={{ mt: 2 }}>
            Game session saved. Check My Journey in the KNLS Student app.
          </Alert>
        )}
      </Container>
    </Box>
  );
}
